import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cva, type CVAVariantProps } from '../../utils/variants';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti dello Stack
 */
const stackVariants = cva(
  'flex',
  {
    variants: {
      direction: {
        vertical: 'flex-col',
        horizontal: 'flex-row',
      },
      spacing: {
        none: 'gap-0',
        xs: 'gap-1',
        sm: 'gap-2',
        md: 'gap-4',
        lg: 'gap-6',
        xl: 'gap-8',
      },
      align: {
        start: 'items-start',
        center: 'items-center',
        end: 'items-end',
        stretch: 'items-stretch',
      },
      justify: {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        between: 'justify-between',
        around: 'justify-around',
        evenly: 'justify-evenly',
      },
    },
    defaultVariants: {
      direction: 'vertical',
      spacing: 'md',
      align: 'stretch',
      justify: 'start',
    },
  }
);

export interface StackProps
  extends HTMLAttributes<HTMLDivElement>,
    BaseComponentProps,
    CVAVariantProps<typeof stackVariants> {
  /**
   * Wrap items su più righe/colonne
   */
  wrap?: boolean;
  /**
   * Divide con divider tra gli elementi
   */
  divider?: boolean;
}

/**
 * Componente Stack
 * 
 * Layout flessibile per stack verticale o orizzontale con spacing configurabile.
 * 
 * @example
 * ```tsx
 * <Stack direction="vertical" spacing="lg">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </Stack>
 * 
 * <Stack direction="horizontal" spacing="md" align="center">
 *   <Button>Action 1</Button>
 *   <Button>Action 2</Button>
 * </Stack>
 * ```
 */
const StackComponent = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      className,
      direction,
      spacing,
      align,
      justify,
      wrap,
      divider,
      children,
      ...props
    },
    ref
  ) => {
    // Se divider è abilitato, aggiunge separatori tra gli elementi
    const childrenArray = divider ? 
      Array.isArray(children) 
        ? children.flatMap((child, index) => 
            index < children.length - 1 
              ? [child, <div key={`divider-${index}`} className={cn(
                  direction === 'vertical' ? 'h-px w-full bg-gray-200' : 'w-px h-full bg-gray-200'
                )} />] 
              : [child]
          )
        : children
      : children;

    return (
      <div
        ref={ref}
        className={cn(
          stackVariants({ direction, spacing: divider ? 'md' : spacing, align, justify }),
          wrap && 'flex-wrap',
          className
        )}
        {...props}
      >
        {childrenArray}
      </div>
    );
  }
);

StackComponent.displayName = 'Stack';

export const Stack = memo(StackComponent);

